/*  MUI  */
import { Badge, Button, Flex } from '@auviknetworks/iceberg-ui';
import { Cancel, CheckCircle, OpenInNew } from '@mui/icons-material';
import {
    Box,
    Card,
    CardActions,
    CardContent,
    Link as ExternalLink,
    Grid,
    Typography
} from '@mui/material';

/*  Shared  */
import { Permission } from 'application/Shared/Components/Core';
import { TruncatedText } from 'application/Shared/Components/TruncatedText';
import { FontSizes } from 'application/Shared/Constants';
import { Route } from 'application/Shared/Helpers';
import { UNIFIED_AGENT, hasFeature } from 'featureFlags/src';

/* Hooks */
import { useUserTheme } from 'hooks/dataAccess/User/userTheme';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

const renderEnabledIcon = ({ canEnable, enabled }) => {
    if (!canEnable) {
        return null;
    }

    if (!enabled) {
        return <Cancel fontSize="small" sx={{ color: 'icon.danger' }} />;
    }

    return <CheckCircle fontSize="small" sx={{ color: 'icon.success' }} />;
};

export const IntegrationCard = ({ integration, onboarding = false }) => {
    const {
        action,
        application,
        available,
        canEnable,
        description,
        enabled,
        externalPageLink,
        image,
        newChip,
        betaChip,
        page,
        permission,
        show
    } = integration;

    const { t } = useTranslation();
    const { partnerId } = useParams();

    const baseRoute = Route.buildBasePartnerRoute({ partnerId });
    const integrationRoute = `${baseRoute}${page}`;

    const userTheme = useUserTheme();

    if (!show) {
        return null;
    }

    return (
        (!betaChip || hasFeature(UNIFIED_AGENT)) && (
            <Permission required={permission}>
                <Grid item xs={12} md={6} lg={4}>
                    <Card
                        variant="outlined"
                        sx={{
                            backgroundColor:
                                'onboarding.integrationCardBackgroundColor',
                            borderColor:
                                'onboarding.integrationCardBorderColor',
                            display:
                                onboarding && !available ? 'none' : 'inherit',
                            height: '100%',
                            opacity: available ? '100%' : '50%',
                            position: 'relative'
                        }}
                    >
                        {newChip && (
                            <Box
                                style={{
                                    left: '24px',
                                    position: 'absolute',
                                    top: '24px'
                                }}
                            >
                                <Badge
                                    variant="warning"
                                    text={t('Common.new').toUpperCase()}
                                />
                            </Box>
                        )}
                        {betaChip && (
                            <Box
                                style={{
                                    left: '24px',
                                    position: 'absolute',
                                    top: '24px'
                                }}
                            >
                                <Badge
                                    variant="warning"
                                    text={t('Common.beta').toUpperCase()}
                                />
                            </Box>
                        )}
                        <Box
                            display="flex"
                            flexDirection="column"
                            height="100%"
                            justifyContent="space-between"
                        >
                            <CardContent>
                                <Box
                                    alignItems="center"
                                    display="flex"
                                    height="100px"
                                    justifyContent="center"
                                    mx="16px"
                                    my="32px"
                                >
                                    <img
                                        alt={application}
                                        id="integrationImage"
                                        src={`/images/${userTheme}/integrations/${image}`}
                                        style={{
                                            maxHeight: '100px',
                                            maxWidth: '100%'
                                        }}
                                    />
                                </Box>
                                {onboarding ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        style={{
                                            fontSize: FontSizes[3],
                                            marginTop: '16px',
                                            textAlign: 'center'
                                        }}
                                    >
                                        {application.includes('Script')
                                            ? application
                                            : t(
                                                  'Integrations.configuration_scripts',
                                                  {
                                                      application
                                                  }
                                              )}
                                    </Typography>
                                ) : (
                                    <TruncatedText
                                        maxLines={3}
                                        text={t(description)}
                                    />
                                )}
                            </CardContent>
                            <CardActions
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginTop: '16px',
                                    minHeight: '45px',
                                    padding: '16px'
                                }}
                            >
                                {!available && (
                                    <Typography size="small">
                                        {t('Common.in_beta')}
                                    </Typography>
                                )}
                                {((available && !externalPageLink) ||
                                    (available &&
                                        externalPageLink &&
                                        onboarding)) && (
                                    <Flex
                                        width="100%"
                                        justifyContent="flex-start"
                                    >
                                        <Link
                                            to={
                                                onboarding
                                                    ? `${baseRoute}/onboarding/deployAgent/configure/${integration.key}`
                                                    : integrationRoute
                                            }
                                            data-trackid={`integrationSelectBtn_${application}_routeTo`}
                                        >
                                            <Button
                                                data-trackid={`integrationSelectBtn_${application}`}
                                            >
                                                {t(action)}
                                            </Button>
                                        </Link>
                                    </Flex>
                                )}
                                {available &&
                                    externalPageLink &&
                                    !onboarding && (
                                        <Flex
                                            width="100%"
                                            justifyContent="center"
                                        >
                                            <ExternalLink
                                                href={externalPageLink}
                                                rel="noopener noreferrer nofollow"
                                                target="_blank"
                                            >
                                                <Button data-trackid="partner_integrations_openExternalLink">
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                    >
                                                        {t(action)}
                                                        <OpenInNew
                                                            style={{
                                                                fontSize:
                                                                    FontSizes[1],
                                                                marginLeft:
                                                                    '4px'
                                                            }}
                                                        />
                                                    </Box>
                                                </Button>
                                            </ExternalLink>
                                        </Flex>
                                    )}
                                {renderEnabledIcon({ canEnable, enabled })}
                            </CardActions>
                        </Box>
                    </Card>
                </Grid>
            </Permission>
        )
    );
};
